import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import ContentManager from "@/pages/admin/ContentManager";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Download,
  LogOut,
  RefreshCw,
  Search,
  Trash2,
  CheckCircle2,
  Circle,
  Mail,
  Phone,
} from "lucide-react";

const KINDS = [
  { key: "contacts", label: "Contacts", search: ["name", "email", "subject", "message"] },
  { key: "training", label: "Training", search: ["full_name", "email", "program", "institution"] },
  { key: "products", label: "Product Enquiries", search: ["name", "email", "product", "company"] },
  { key: "careers", label: "Careers", search: ["full_name", "email", "position"] },
  { key: "newsletter", label: "Newsletter", search: ["email"] },
];

const COLS = {
  contacts: ["created_at", "name", "email", "phone", "subject", "message"],
  training: ["created_at", "full_name", "email", "phone", "program", "institution", "education_level", "mode", "message"],
  products: ["created_at", "product", "name", "email", "company", "phone", "quantity", "message"],
  careers: ["created_at", "position", "full_name", "email", "phone", "experience_years", "linkedin", "portfolio", "cover_letter"],
  newsletter: ["created_at", "email"],
};

const humanize = (k) =>
  k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

const truncate = (v, n = 80) => {
  const s = String(v ?? "");
  return s.length > n ? s.slice(0, n) + "…" : s;
};

const toCSV = (rows, cols) => {
  const header = cols.map(humanize).join(",");
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const body = rows.map((r) => cols.map((c) => escape(r[c])).join(","));
  return [header, ...body].join("\n");
};

const AdminDashboard = () => {
  useDocumentTitle("Admin Dashboard");
  const { email, logout } = useAuth();
  const navigate = useNavigate();

  const [active, setActive] = useState("contacts");
  const [view, setView] = useState("submissions"); // 'submissions' | 'content'
  const [data, setData] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [contacts, training, products, careers, newsletter, statsResp] =
        await Promise.all([
          api.get("/admin/contacts"),
          api.get("/admin/training"),
          api.get("/admin/products"),
          api.get("/admin/careers"),
          api.get("/admin/newsletter"),
          api.get("/admin/stats"),
        ]);
      setData({
        contacts: contacts.data,
        training: training.data,
        products: products.data,
        careers: careers.data,
        newsletter: newsletter.data,
      });
      setStats(statsResp.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
        navigate("/admin/login", { replace: true });
      } else {
        toast.error("Failed to load submissions");
      }
    } finally {
      setLoading(false);
    }
  }, [logout, navigate]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const kind = useMemo(() => KINDS.find((k) => k.key === active), [active]);

  const filtered = useMemo(() => {
    const rows = data[active] || [];
    if (!query.trim()) return rows;
    const q = query.trim().toLowerCase();
    const searchFields = kind?.search || [];
    return rows.filter((r) =>
      searchFields.some((f) => String(r[f] || "").toLowerCase().includes(q))
    );
  }, [data, active, query, kind]);

  const rows = data[active] || [];

  const onMarkRead = async (id) => {
    try {
      await api.patch(`/admin/${active}/${id}/read`);
      setData((d) => ({
        ...d,
        [active]: d[active].map((r) => (r.id === id ? { ...r, read: true } : r)),
      }));
      toast.success("Marked as read");
    } catch {
      toast.error("Failed to update");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    try {
      await api.delete(`/admin/${active}/${id}`);
      setData((d) => ({
        ...d,
        [active]: d[active].filter((r) => r.id !== id),
      }));
      toast.success("Deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const onExport = () => {
    const cols = COLS[active];
    const csv = toCSV(filtered, cols);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `akyvia-${active}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  const onLogout = () => {
    logout();
    toast.success("Signed out");
    navigate("/admin/login", { replace: true });
  };

  return (
    <div data-testid="admin-dashboard-page" className="min-h-[calc(100vh-160px)] bg-slate-50/60">
      {/* Admin header */}
      <div className="bg-[#0B1B3D] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src="/brand/akyvia-logo.png"
              alt="Akyvia"
              className="w-11 h-11 rounded-full object-contain ring-1 ring-white/15"
            />
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-sky-300/80">
                Akyvia · Admin Console
              </div>
              <div className="font-display text-lg font-semibold">
                Submissions dashboard
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-1 mr-2 bg-white/5 border border-white/10 rounded-md p-1">
              <button
                data-testid="admin-view-submissions"
                onClick={() => setView("submissions")}
                className={`text-xs px-3 py-1.5 rounded ${
                  view === "submissions"
                    ? "bg-amber-500 text-[#0B1B3D] font-semibold"
                    : "text-slate-200 hover:text-white"
                }`}
              >
                Submissions
              </button>
              <button
                data-testid="admin-view-content"
                onClick={() => setView("content")}
                className={`text-xs px-3 py-1.5 rounded ${
                  view === "content"
                    ? "bg-amber-500 text-[#0B1B3D] font-semibold"
                    : "text-slate-200 hover:text-white"
                }`}
              >
                Content
              </button>
            </div>
            <div className="hidden md:block text-xs text-slate-300">
              Signed in as <span className="text-amber-400">{email}</span>
            </div>
            <button
              data-testid="admin-refresh-button"
              onClick={loadAll}
              disabled={loading}
              className="inline-flex items-center gap-2 border border-white/20 hover:border-amber-400 text-white text-sm px-3.5 py-2 rounded-md disabled:opacity-60"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={onLogout}
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-[#0B1B3D] text-sm font-medium px-3.5 py-2 rounded-md"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {view === "content" ? (
          <ContentManager />
        ) : (
        <>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          {KINDS.map((k) => (
            <button
              key={k.key}
              data-testid={`admin-stat-${k.key}`}
              onClick={() => setActive(k.key)}
              className={`text-left border rounded-xl p-4 transition-all ${
                active === k.key
                  ? "border-[#0B1B3D] bg-white shadow-md"
                  : "border-slate-200 bg-white hover:border-sky-300"
              }`}
            >
              <div className="text-[10px] uppercase tracking-widest text-slate-500">
                {k.label}
              </div>
              <div className="mt-1 font-display text-3xl font-semibold text-[#0B1B3D]">
                {stats ? stats[k.key] ?? 0 : (data[k.key]?.length ?? 0)}
              </div>
            </button>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={active} onValueChange={setActive}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <TabsList
              data-testid="admin-tabs"
              className="bg-white border border-slate-200 p-1 rounded-lg overflow-x-auto"
            >
              {KINDS.map((k) => (
                <TabsTrigger
                  key={k.key}
                  value={k.key}
                  data-testid={`admin-tab-${k.key}`}
                  className="text-sm data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white"
                >
                  {k.label}
                  <Badge
                    variant="secondary"
                    className="ml-2 bg-slate-100 text-slate-700 data-[state=active]:bg-white/10 data-[state=active]:text-white"
                  >
                    {data[k.key]?.length ?? 0}
                  </Badge>
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="admin-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search…"
                  className="pl-9 w-56 bg-white"
                />
              </div>
              <button
                data-testid="admin-export-csv"
                onClick={onExport}
                className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:border-[#0B1B3D] text-slate-700 hover:text-[#0B1B3D] text-sm px-3.5 py-2 rounded-md"
              >
                <Download className="w-3.5 h-3.5" />
                Export CSV
              </button>
            </div>
          </div>

          {KINDS.map((k) => (
            <TabsContent key={k.key} value={k.key} className="mt-0">
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                {filtered.length === 0 ? (
                  <div
                    data-testid={`admin-empty-${k.key}`}
                    className="py-24 text-center text-slate-500"
                  >
                    <div className="text-sm">No submissions yet.</div>
                    <div className="text-xs mt-1 text-slate-400">
                      New {k.label.toLowerCase()} will appear here.
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="w-10"></TableHead>
                          {COLS[k.key].map((c) => (
                            <TableHead key={c} className="text-xs uppercase tracking-widest text-slate-500">
                              {humanize(c)}
                            </TableHead>
                          ))}
                          <TableHead className="w-24 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map((r) => (
                          <TableRow key={r.id} data-testid={`admin-row-${r.id}`} className="align-top">
                            <TableCell>
                              {r.read ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                              ) : (
                                <Circle className="w-4 h-4 text-amber-500 fill-amber-100" />
                              )}
                            </TableCell>
                            {COLS[k.key].map((c) => (
                              <TableCell
                                key={c}
                                className={`text-sm ${
                                  c === "created_at"
                                    ? "text-slate-500 font-mono-akyvia text-xs whitespace-nowrap"
                                    : "text-slate-700"
                                }`}
                              >
                                {c === "created_at"
                                  ? formatDate(r[c])
                                  : c === "email" ? (
                                    <a href={`mailto:${r[c]}`} className="text-sky-600 hover:underline inline-flex items-center gap-1">
                                      <Mail className="w-3 h-3" />
                                      {r[c]}
                                    </a>
                                  ) : c === "phone" ? (
                                    r[c] ? (
                                      <a href={`tel:${r[c]}`} className="text-slate-700 hover:text-[#0B1B3D] inline-flex items-center gap-1">
                                        <Phone className="w-3 h-3" />
                                        {r[c]}
                                      </a>
                                    ) : "—"
                                  ) : (
                                    truncate(r[c])
                                  )}
                              </TableCell>
                            ))}
                            <TableCell className="text-right">
                              <div className="inline-flex items-center gap-1">
                                {!r.read && (
                                  <button
                                    data-testid={`admin-mark-read-${r.id}`}
                                    onClick={() => onMarkRead(r.id)}
                                    className="p-1.5 rounded hover:bg-slate-100 text-emerald-600"
                                    title="Mark as read"
                                  >
                                    <CheckCircle2 className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  data-testid={`admin-delete-${r.id}`}
                                  onClick={() => onDelete(r.id)}
                                  className="p-1.5 rounded hover:bg-slate-100 text-rose-600"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
        </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
